import React, { useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  type GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { DetailRow } from "kontrolEdge/components/Shared/DetailRow";
import { EditableTagList } from "kontrolEdge/components/Shared/NameTagEdit/EditableTagList";
import ServiceStatusValue from "kontrolEdge/components/Shared/ServiceStatusValue";
import {
  ParentConnectionLink,
  accessTypeLabel,
  carrierAccessStatusPresentation,
  formatParentConnection,
} from "../CarrierAccessData";
import { updateCarrierAccessName } from "../requests/request";
import { legHasReading, legStatusTag } from "./carrierAccessLegs";
import CarrierLegBandwidthChart from "./CarrierLegBandwidthChart";
import { useCarrierAccessDetails } from "./useCarrierAccessDetails";
import type {
  CarrierAccessDetail,
  CarrierAccessEndpoint,
  CarrierAccessLeg,
} from "../carrierAccess.types";

/** Axios errors carry the backend's `{ message }`; fall back to the error. */
const saveErrorMessage = (err: unknown): string => {
  const message = (err as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  if (message) return message;
  return err instanceof Error ? err.message : "Could not save the change";
};

// Carrier Access is tagged only through TaggedResources (CTX-654), so the
// picker path of EditableTagList is the only one used; the legacy handler
// exists just to satisfy the combined prop contract — as in Extended PoP.
const legacyTagsNoop = async () => undefined;

const LoadingCard: React.FC = () => (
  <GenericCard
    isCollapsible={false}
    bodyContent={
      <div className="flex flex-col gap-3 py-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonLine key={i} className="h-4 w-full" />
        ))}
      </div>
    }
  />
);

interface EditRowProps {
  detail: CarrierAccessDetail;
  tenantId: number;
  onSaved: (changes: Partial<CarrierAccessDetail>) => void;
}

/** Name, editable in place like Port Details' Name pill. */
const NameRow: React.FC<EditRowProps> = ({ detail, tenantId, onSaved }) => {
  const api = useAxiosAuth0();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (next: string) => {
    const name = next.trim();
    if (!name || name === detail.name) return;
    setIsSaving(true);
    setError(null);
    try {
      await updateCarrierAccessName(api, tenantId, detail.id, name);
      onSaved({ name });
    } catch (err) {
      setError(saveErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DetailRow
      label="Name"
      value={
        <div className="flex flex-col items-end gap-1">
          <GenericTag
            label={detail.name || "—"}
            variant="neutral"
            size="small"
            minWidth="100px"
            className="text-center"
            editable
            value={detail.name}
            onSave={save}
            isSaving={isSaving}
            disabled={isSaving}
          />
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      }
    />
  );
};

/** The parent connection(s), each linking to its own detail view. */
const ParentConnectionValue: React.FC<{
  endpoints: CarrierAccessEndpoint[];
}> = ({ endpoints }) => {
  if (endpoints.length === 0) return null;
  return (
    <div className="flex flex-col items-end gap-0.5">
      {endpoints.map((endpoint) => (
        <ParentConnectionLink
          key={`${endpoint.type}:${endpoint.id}`}
          endpoint={endpoint}
        />
      ))}
    </div>
  );
};

const legColumns: GenericTableColumn<CarrierAccessLeg>[] = [
  { header: "Carrier", accessor: "name" },
  {
    header: "Circuit",
    renderCell: (leg) => leg.circuitId ?? "-",
    sortAccessor: (leg) => leg.circuitId ?? "",
  },
  {
    header: "Service Request",
    renderCell: (leg) => leg.serviceRequestId ?? "-",
    sortAccessor: (leg) => leg.serviceRequestId ?? "",
  },
  {
    header: "Parent Connection",
    renderCell: (leg) =>
      leg.endpoint ? <ParentConnectionLink endpoint={leg.endpoint} /> : "-",
    sortAccessor: (leg) =>
      leg.endpoint ? formatParentConnection(leg.endpoint) : "",
  },
  {
    header: "Status",
    renderCell: (leg) => {
      const { label, variant } = legStatusTag(leg.status);
      return (
        <ServiceStatusValue
          label={label}
          variant={variant}
          tooltip={leg.statusMessage ?? undefined}
        />
      );
    },
    sortAccessor: (leg) => legStatusTag(leg.status).label,
  },
];

/**
 * The carriers of the access, read-only: the reconcilers derive them from the
 * descriptions networking writes on the routers, and only the last mile is
 * served (a trunk's CARR_ is the backbone's). Same table the other detail
 * views use for theirs (P2P Addressing, BGP Summary). How a leg was read off
 * the routers is not the customer's concern; which carrier is active and
 * which standby is (Jose, PR #696).
 */
const LegsCard: React.FC<{ legs: CarrierAccessLeg[] }> = ({ legs }) => (
  <GenericCard
    isCollapsible
    initialExpanded
    headerContent="Carriers"
    bodyContent={
      legs.length === 0 ? (
        <p className="py-1.5 text-sm text-semantic-text-secondary">
          No carriers are linked to this access yet.
        </p>
      ) : (
        <GenericTable
          columns={legColumns}
          data={legs}
          tableClassName="w-full"
          isFooterVisible={false}
        />
      )
    }
  />
);

/**
 * One card per carrier, read from its VLAN. The access as a whole is the
 * parent Port's own chart (Jose, PR #696), so it is not repeated here. Only
 * legs with a reading are plotted.
 */
const CarrierAccessCharts: React.FC<{
  detail: CarrierAccessDetail;
  tenantId: number;
}> = ({ detail, tenantId }) => {
  const plotted = detail.legs.filter(legHasReading);
  if (plotted.length === 0) return null;

  return (
    <div className="flex flex-col gap-4 pb-4">
      {plotted.map((leg) => (
        <CarrierLegBandwidthChart
          key={`${leg.kind}:${leg.id}`}
          carrierAccessId={detail.id}
          leg={leg}
          title={leg.name}
          tenantId={tenantId}
        />
      ))}
    </div>
  );
};

/**
 * Carrier Access — detail page (Kontrol Edge, CTX-653). Homologated with the
 * Extended PoP and Port detail: a grid of cards, details left and Status
 * right, then the legs. Closes the project condition: each access declares
 * its type and its two ends.
 */
const CarrierAccessDetails: React.FC = () => {
  const { tenantId } = useAppSelector((state) => state.selection);
  const { detail, loading, error, patch } = useCarrierAccessDetails();

  if (error) {
    return <ErrorFetchingItems text={error} />;
  }

  if (loading || !detail) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 px-4">
        <LoadingCard />
        <LoadingCard />
      </div>
    );
  }

  const status = carrierAccessStatusPresentation(detail.status);

  return (
    <div className="px-4 overflow-y-auto h-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="Carrier Access Details"
            bodyContent={
              <div className="space-y-2">
                <DetailRow label="ID" value={detail.serviceId} />
                <NameRow detail={detail} tenantId={tenantId} onSaved={patch} />
                <DetailRow
                  label="Tag"
                  value={
                    <EditableTagList
                      tags={[]}
                      onUpdate={legacyTagsNoop}
                      resourceType="ke-carrier-access"
                      resourceId={detail.id}
                      tenantId={detail.tenantId}
                    />
                  }
                />
                {/* Read-only: the commercial type is corrected from the
                    Admin Center, with the alta and the baja (Jose, PR #696). */}
                <DetailRow
                  label="Type"
                  value={accessTypeLabel(detail.accessType)}
                />
                <DetailRow label="Customer" value={detail.customer?.name} />
                <DetailRow
                  label="Parent Connection"
                  value={<ParentConnectionValue endpoints={detail.endpoints} />}
                  isLast
                />
              </div>
            }
          />
        </div>

        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="Status"
            bodyContent={
              <div className="space-y-2">
                <DetailRow
                  label="Service Status"
                  value={
                    <GenericTag
                      variant={status.variant}
                      label={status.label}
                      size="small"
                    />
                  }
                />
                <DetailRow
                  label="Term"
                  value={detail.term === "-" ? null : detail.term}
                />
                <DetailRow
                  label="Auto Renew"
                  value={detail.autoRenew === "-" ? null : detail.autoRenew}
                />
                <DetailRow
                  label="Renew Date"
                  value={detail.renewDate === "-" ? null : detail.renewDate}
                />
                <DetailRow
                  label="Subscription Status"
                  value={
                    detail.subscriptionStatus === "-"
                      ? null
                      : detail.subscriptionStatus
                  }
                  isLast
                />
              </div>
            }
          />
        </div>
      </div>

      <div className="pb-4">
        <LegsCard legs={detail.legs} />
      </div>

      <CarrierAccessCharts detail={detail} tenantId={tenantId} />
    </div>
  );
};

export default CarrierAccessDetails;
